import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  VStack,
  Avatar,
  Badge,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { updateMessage } from "../services/updateSupport";
import { sendUserResponse } from "../services/getMessages";
import { timeago } from "../../topUp/utils/timeAgo";
import dayjs from "dayjs";

// A single chat bubble — right-aligned for user, left-aligned for admin
const MessageBubble = ({ msg, date, isUser, name, pageColor }) => (
  <Flex justify={isUser ? "flex-end" : "flex-start"} mb="10px" align="flex-end">
    {!isUser && (
      <Avatar name={name || "Admin"} size="xs" mr="8px" flexShrink={0} />
    )}
    <Box
      maxW="75%"
      bg={isUser ? pageColor || "#2A9D8F" : "#F0F0F0"}
      color={isUser ? "#FFFFFF" : "#264653"}
      px="14px"
      py="10px"
      borderRadius={isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px"}
    >
      <Text fontSize="14px" lineHeight={1.5}>
        {msg}
      </Text>
      <Text
        fontSize="11px"
        opacity={0.65}
        mt="4px"
        textAlign={isUser ? "right" : "left"}
      >
        {timeago(date)}
      </Text>
    </Box>
    {isUser && (
      <Avatar name={name || "You"} size="xs" ml="8px" flexShrink={0} />
    )}
  </Flex>
);

// One ticket card — expandable to show the full thread + reply input
const TicketCard = ({ data, setRefresh, pageColor }) => {
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const handleReply = () => {
    if (!replyText.trim()) return;
    setReplying(true);
    sendUserResponse(data._id, { message: replyText }, setReplying, setRefresh);
    setReplyText("");
  };

  const lastReply = data?.response?.[data.response.length - 1];

  return (
    <Box bg="#FFFFFF" rounded="lg" mb="14px" shadow="sm" overflow="hidden">
      {/* Header row — click to expand */}
      <Flex
        align="center"
        justify="space-between"
        px="20px"
        py="14px"
        cursor="pointer"
        onClick={() => setExpanded((p) => !p)}
        _hover={{ bg: "#F9FEFF" }}
      >
        <Box flex={1} mr="12px">
          <Text
            fontWeight="semibold"
            fontSize="14px"
            color="#264653"
            noOfLines={1}
          >
            {data?.reason || "Support Ticket"}
          </Text>
          <Text fontSize="12px" color="#808080" mt="2px">
            {dayjs(data?.createdAt).format("DD MMM YYYY")} &middot;{" "}
            {data?.response?.length || 0}{" "}
            {data?.response?.length === 1 ? "reply" : "replies"}
            {lastReply && ` · Last: ${timeago(lastReply.date)}`}
          </Text>
        </Box>
        <Badge
          colorScheme={data?.status === "RESOLVED" ? "green" : "orange"}
          fontSize="10px"
          mr="10px"
        >
          {data?.status}
        </Badge>
        <Text color={pageColor} fontSize="18px" fontWeight="bold">
          {expanded ? "−" : "+"}
        </Text>
      </Flex>

      <Collapse in={expanded} animateOpacity>
        <Box px="20px" pb="18px" borderTop="1px solid #F5F5F5">
          <Box mt="14px">
            {/* User's original message */}
            <MessageBubble
              msg={data?.description}
              date={data?.createdAt}
              isUser={true}
              name={data?.userId?.name || "You"}
              pageColor={pageColor}
            />

            {/* All replies in the thread */}
            {data?.response?.map((res, i) => (
              <MessageBubble
                key={res?._id || i}
                msg={res?.message}
                date={res?.date}
                isUser={res?.senderType === "User"}
                name={
                  res?.senderType === "User"
                    ? data?.userId?.name || "You"
                    : "Admin"
                }
                pageColor={pageColor}
              />
            ))}
          </Box>

          {/* Reply box — hide when resolved */}
          {data?.status !== "RESOLVED" && (
            <Box mt="14px">
              <Textarea
                placeholder="Type a reply…"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                size="sm"
                resize="none"
                h="80px"
                bg="#F9FEFF"
                rounded="md"
                fontSize="14px"
              />
              <Flex justify="flex-end" mt="8px">
                <Button
                  size="sm"
                  bg={pageColor}
                  color="#FFFFFF"
                  _hover={{ opacity: 0.85 }}
                  isLoading={replying}
                  onClick={handleReply}
                  isDisabled={!replyText.trim()}
                >
                  Reply
                </Button>
              </Flex>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export const Message = ({ response, setRefresh, estateId }) => {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    if (!reason.trim() || !description.trim()) return;
    setLoading(true);
    updateMessage({ reason, description, estateId }, setLoading, setRefresh);
    setReason("");
    setDescription("");
  };

  const pageColor = useSelector(
    (state) => state?.switchTab?.brandColor?.brandColor,
  );

  return (
    <>
      <Flex align="center" justify="space-between" w="95%">
        <Text color="#264653" fontWeight="bold" fontSize="30px" py="10px">
          Messages
        </Text>
      </Flex>

      <Flex direction={["column", "column", "row"]}>
        {/* Left: New ticket form */}
        <Box
          bg="#FFFFFF"
          p="30px"
          w={["100%", "100%", "38%"]}
          rounded={7}
          mr={["0", "0", "30px"]}
          mb={["24px", "24px", "0"]}
          alignSelf="flex-start"
        >
          <VStack alignItems="flex-start" spacing={2}>
            <Text color="#264653" fontSize="22px" fontWeight="bold">
              New Support Ticket
            </Text>
            <Text color="#264653" fontSize="14px" fontWeight={500}>
              Got any issues? We're here to help.
            </Text>
            <Text color={pageColor} fontSize="13px" fontWeight={500}>
              Fill the form below to open a ticket.
            </Text>
            <Divider pt="8px" />
          </VStack>

          <FormControl mt="8px">
            <FormLabel color="#9B9B9B" fontSize="12px" pt="12px">
              Reason
            </FormLabel>
            <Input
              fontSize={"14px"}
              bg="#F9FEFF"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <FormLabel color="#9e7d7d" fontSize="12px" pt="12px">
              Description
            </FormLabel>
            <Textarea
              size="lg"
              resize="none"
              h="130px"
              fontSize={"14px"}
              bg="#F9FEFF"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormControl>

          <Button
            bg={"primary.500"}
            w="100%"
            mt="20px"
            mb="10px"
            isLoading={loading}
            isDisabled={!reason.trim() || !description.trim()}
            onClick={handleSubmit}
          >
            Send
          </Button>
        </Box>

        {/* Right: Ticket thread list */}
        <Box
          w={["100%", "100%", "60%"]}
          h={["auto", "auto", "80vh"]}
          overflowY="auto"
          pb="40px"
        >
          <Text color="#264653" fontSize="16px" fontWeight="bold" mb="14px">
            Your Tickets
          </Text>

          {response?.length > 0 ? (
            response.map((data, i) => (
              <TicketCard
                key={data?._id || i}
                data={data}
                setRefresh={setRefresh}
                pageColor={pageColor}
              />
            ))
          ) : (
            <Box bg="#FFFFFF" p="30px" rounded="lg" textAlign="center">
              <Text color="#808080" fontSize="15px">
                No messages yet. Submit a ticket to get started.
              </Text>
            </Box>
          )}
        </Box>
      </Flex>
    </>
  );
};
